import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/auth-store";
import type { LoginResponseDto } from "@finora/types";

type LoginMode = "password" | "otp";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const [mode, setMode] = useState<LoginMode>("password");

  // Nakon uspešne prijave (lozinka ili OTP) - ako korisnik nema firmu
  // (hasCompany=false, npr. potvrdio nalog ali nije završio onboarding),
  // ide na /onboarding umesto na stranicu sa koje je stigao/dashboard - vidi
  // razgovor "CompanyStep pravi firmu". Vidi i pending-user.decorator.ts.
  function onLoginSuccess(data: LoginResponseDto) {
    login(data.accessToken, data.user, data.companyName, data.hasCompany);
    if (!data.hasCompany) {
      navigate("/onboarding", { replace: true });
      return;
    }
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    navigate(from, { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-6 shadow-subtle">
        <h1 className="mb-1 text-xl font-semibold text-slate-900">HAFiskal</h1>
        <p className="mb-4 text-sm text-slate-500">Prijavite se na svoj nalog</p>

        <div className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-app-bg p-1">
          <button
            type="button"
            onClick={() => setMode("password")}
            className={`rounded-md py-1.5 text-sm font-medium transition-colors ${
              mode === "password" ? "bg-surface text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            Lozinka
          </button>
          <button
            type="button"
            onClick={() => setMode("otp")}
            className={`rounded-md py-1.5 text-sm font-medium transition-colors ${
              mode === "otp" ? "bg-surface text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            OTP kod
          </button>
        </div>

        {mode === "password" ? (
          <PasswordLoginForm onSuccess={onLoginSuccess} />
        ) : (
          <OtpLoginForm onSuccess={onLoginSuccess} />
        )}

        <p className="mt-4 text-center text-sm text-slate-500">
          Nemate nalog?{" "}
          <Link to="/registracija" className="text-brand hover:underline">
            Registrujte firmu
          </Link>
        </p>
      </div>
    </div>
  );
}

function PasswordLoginForm({ onSuccess }: { onSuccess: (data: LoginResponseDto) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const mutation = useMutation({
    mutationFn: () => api.login({ email, password }),
    onSuccess,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            required
          />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="block text-sm font-medium text-slate-700">Lozinka</label>
            <Link to="/zaboravljena-lozinka" className="text-xs text-brand hover:underline">
              Zaboravili ste lozinku?
            </Link>
          </div>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            required
          />
        </div>

        {mutation.isError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {mutation.error instanceof ApiError
              ? mutation.error.response.message
              : "Prijava nije uspela. Pokušajte ponovo."}
          </p>
        )}

        <Button type="submit" className="w-full" disabled={mutation.isPending}>
          {mutation.isPending ? "Prijavljivanje..." : "Prijavi se"}
        </Button>
      </div>
    </form>
  );
}

/**
 * Prijava preko OTP koda na mejl - vidi razgovor "korisnik moze za login da
 * bira hoce li otp ili sifru". Dva koraka: pošalji kod na email, pa unesi
 * 6-cifreni kod - isti krajnji rezultat (JWT) kao prijava lozinkom.
 */
function OtpLoginForm({ onSuccess }: { onSuccess: (data: LoginResponseDto) => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  const requestCode = useMutation({
    mutationFn: () => api.requestOtp({ email }),
    onSuccess: () => setCodeSent(true),
  });

  const verifyCode = useMutation({
    mutationFn: () => api.verifyOtp({ email, code }),
    onSuccess,
  });

  if (!codeSent) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          requestCode.mutate();
        }}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              required
            />
          </div>

          {requestCode.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {requestCode.error instanceof ApiError
                ? requestCode.error.response.message
                : "Slanje koda nije uspelo. Pokušajte ponovo."}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={requestCode.isPending}>
            {requestCode.isPending ? "Šaljem kod..." : "Pošalji kod na email"}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        verifyCode.mutate();
      }}
    >
      <div className="space-y-4">
        <p className="rounded-lg bg-brand-light px-3 py-2 text-sm text-brand">
          Poslali smo 6-cifreni kod na <strong>{email}</strong>.
        </p>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Kod</label>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            className="h-10 w-full rounded-lg border border-border px-3 text-center text-lg tracking-[0.3em] focus:border-brand focus:outline-none"
            required
          />
        </div>

        {verifyCode.isError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {verifyCode.error instanceof ApiError
              ? verifyCode.error.response.message
              : "Prijava nije uspela. Pokušajte ponovo."}
          </p>
        )}

        <Button type="submit" className="w-full" disabled={verifyCode.isPending || code.length !== 6}>
          {verifyCode.isPending ? "Prijavljivanje..." : "Prijavi se"}
        </Button>

        <button
          type="button"
          onClick={() => {
            setCodeSent(false);
            setCode("");
          }}
          className="w-full text-center text-sm text-slate-500 hover:underline"
        >
          Pošalji na drugi email / pošalji ponovo
        </button>
      </div>
    </form>
  );
}
